import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import ResumeDownload from './ResumeDownload';

type ProjectDevice = 'phone' | 'mac';

interface Project {
    title: string;
    skills: string[];
    device: ProjectDevice;
    appSrc: string;
    splashSrc?: string;
    descKey: string;
    url?: string;
    urlText?: string;
    url2?: string;
    url2Text?: string;
}

interface ProjectCategory {
    title: string;
    icon: string;
    projects: Project[];
}

const CATEGORIES: ProjectCategory[] = [
    {
        title: "Mobile",
        icon: "📱",
        projects: [
            {
                title: "Aeropuerto El Dorado",
                skills: ["Flutter", "Dart"],
                device: "phone",
                appSrc: "/proyectos/dorado.jpg",
                descKey: "proj_desc_dorado",
                url: "https://play.google.com/store/apps/details?id=com.plm.opain.eldorado&hl=es_CO",
            },
            {
                title: "Cuidarte",
                skills: ["React Native", "Expo", "Laravel"],
                device: "phone",
                appSrc: "/proyectos/cuidarte.jpg",
                descKey: "proj_desc_cuidarte"
            },
            {
                title: "Mi finca verde",
                skills: ["React Native"],
                device: "phone",
                appSrc: "/proyectos/mifincaverde.jpg",
                descKey: "proj_desc_mifincaverde"
            }
        ]
    },
    {
        title: "JavaScript",
        icon: "⚡",
        projects: [
            {
                title: "Sistepos",
                skills: ["React", "Firebase", "Electron"],
                device: "mac",
                appSrc: "/proyectos/sistepos.png",
                descKey: "proj_desc_sistepos",
                url: "https://sistepos.com/"
            },
            {
                title: "Cavas",
                skills: ["React", "AWS"],
                device: "mac",
                appSrc: "/proyectos/sistepos.png",
                descKey: "proj_desc_cavas"
            }
        ]
    },
    {
        title: "Three.js · 3D",
        icon: "🌐",
        projects: [
            {
                title: "Hexa Software",
                skills: ["React", "THREE.js", "Vite"],
                device: "mac",
                appSrc: "/proyectos/hexa.png",
                descKey: "proj_desc_hexa",
                url: "https://hexasoftware.net/"
            },
            {
                title: "Descarga Sistepos",
                skills: ["React", "THREE.js", "Vite"],
                device: "mac",
                appSrc: "/proyectos/download.png",
                descKey: "proj_desc_descarga",
                url: "https://download.sistepos.com/"
            },
            {
                title: "Portafolio",
                skills: ["React", "THREE.js", "Vite"],
                device: "mac",
                appSrc: "/proyectos/port.png",
                descKey: "proj_desc_port"
            }
        ]
    },
    {
        title: "WordPress",
        icon: "🖊️",
        projects: [
            {
                title: "Pintuco · Protecto",
                skills: ["WordPress", "Gutenberg"],
                device: "mac",
                appSrc: "/proyectos/pintuco.png",
                descKey: "proj_desc_pintuco",
                url: "https://www.pintuco.com",
                urlText: "Pintuco",
                url2: "https://protectopinturas.com/",
                url2Text: "Protecto"
            },
            {
                title: "Corpo Ceam",
                skills: ["WordPress", "PHP", "Javascript"],
                device: "mac",
                appSrc: "/proyectos/ceam.jpg",
                splashSrc: "/proyectos/splashceam.jpg",
                descKey: "proj_desc_ceam",
                url: "https://corpoceam.org/"
            },
            {
                title: "Ubuntu",
                skills: ["WordPress", "PHP", "Javascript"],
                device: "mac",
                appSrc: "/proyectos/ubuntu.jpg",
                splashSrc: "/proyectos/splashubuntu.jpg",
                descKey: "proj_desc_ubuntu",
                url: "https://cartillaubuntu.com/"
            }
        ]
    },
    {
        title: "Shopify",
        icon: "🛒",
        projects: [
            {
                title: "Talsa",
                skills: ["Liquid", "Javascript"],
                device: "mac",
                appSrc: "/proyectos/talsa.png",
                descKey: "proj_desc_talsa",
                url: "https://talsa.com.co/"
            },
            {
                title: "Scribe",
                skills: ["Liquid", "Javascript"],
                device: "mac",
                appSrc: "/proyectos/scribe.png",
                descKey: "proj_desc_scribe",
                url: "https://www.scribe.com.co/"
            },
            {
                title: "Alico",
                skills: ["Liquid", "Javascript"],
                device: "mac",
                appSrc: "/proyectos/alico.png",
                descKey: "proj_desc_alico",
                url: "https://alicoempaques.com/"
            }
        ]
    },
    {
        title: "Laravel",
        icon: "🔷",
        projects: [
            {
                title: "Marinilla.city",
                skills: ["Laravel", "Javascript"],
                device: "mac",
                appSrc: "/proyectos/marinilla.jpg",
                splashSrc: "/proyectos/splashmarinilla.jpg",
                descKey: "proj_desc_marinilla"
            }
        ]
    }
];

export interface ProjectsProps { }

const Projects: React.FC<ProjectsProps> = () => {
    const { t } = useLanguage();

    return (
        <section className="ultimosTrabajos" id="proyectos">
            {CATEGORIES.map((category, catIdx) => (
                <div key={catIdx} className="win7-category-section">
                    <div className="ancho">

                        {/* Category header */}
                        <div className="win7-category-header">
                            {/* <span className="win7-category-icon">{category.icon}</span> */}
                            <h2 className="win7-category-label">{category.title}</h2>
                        </div>

                        {/* Projects grid */}
                        <div className="win7-projects-grid">
                            {category.projects.map((proj, projIdx) => (
                                <div className="caja win7-project-card" key={projIdx}>
                                    <div className="content">

                                        {/* Header: title + tech pills */}
                                        <div className="cabezote">
                                            <p className="project-title">{proj.title}</p>
                                            <div className="habilidades custom-pills">
                                                {proj.skills.map((skill, sIdx) => (
                                                    <p key={sIdx}>{skill}</p>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Device mockup with scroll animation */}
                                        <div className="mockup-container">
                                            <div className={`dispositivo ${proj.device}`}>
                                                <img
                                                    src={proj.device === 'phone' ? "/iphone.png" : "/mac.png"}
                                                    alt={`${proj.device} frame`}
                                                />
                                                <div className="proyecto">
                                                    {proj.splashSrc && (
                                                        <img
                                                            src={proj.splashSrc}
                                                            alt="Splash screen"
                                                            className="splash"
                                                        />
                                                    )}
                                                    <img
                                                        src={proj.appSrc}
                                                        alt={`${proj.title} UI`}
                                                        className={proj.device === 'phone' ? "app" : "sitio"}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <div className="extract font-large">
                                            <p>{t(proj.descKey)}</p>
                                        </div>

                                        {/* Action buttons */}
                                        <div className="actions">
                                            {proj.url && (
                                                <a
                                                    href={proj.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="verMas win-btn"
                                                >
                                                    {proj.urlText ? proj.urlText : t('proj_see_more')}
                                                </a>
                                            )}
                                            {proj.url2 && (
                                                <a
                                                    href={proj.url2}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="verMas win-btn"
                                                >
                                                    {proj.url2Text}
                                                </a>
                                            )}
                                        </div>

                                    </div>
                                    {/* Keep the .fondo span for backward compat — hidden via CSS */}
                                    <span className="fondo"></span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ))}
            <div className="projects-bottom-container">
                <p className="warning-text">{t('proj_warning')}</p>
                <div className="resume-section-wrapper">
                    <ResumeDownload variant="card" />
                </div>
            </div>
        </section>
    );
};

export default Projects;
